// @ts-nocheck
/*
 * Port of views/dashboard/contact-group/create-all-contacts.js (origin/main-dev, 444 lines).
 * BYTE-IDENTICAL — differs from ContactCreatePage (per-group) in three ways:
 * (1) no useParams id → no contact_group_id in payload, (2) navigates to
 * `/all-contacts` after success (not `/contact-group/:id`), (3) permissive
 * input handler (no regex on text fields, only digit-only slice for phone).
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { FiArrowLeft, FiPlus } from 'react-icons/fi';

// Micro-shims for react-bootstrap Form.* / Row / Col / Container — see
// ContactCreatePage.tsx for the same helper block.
const cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Container: any = ({ className, children, ...props }: any) => (
  <div className={cx('container-fluid', className)} {...props}>
    {children}
  </div>
);
const Row: any = ({ className, children, ...props }: any) => (
  <div className={cx('row', className)} {...props}>
    {children}
  </div>
);
const buildColCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts: string[] = [];
  const push = (bp: string, val: any) => {
    if (val === undefined || val === null || val === false) return;
    parts.push(bp === 'xs' ? `col-${val}` : `col-${bp}-${val}`);
  };
  push('xs', xs);
  push('sm', sm);
  push('md', md);
  push('lg', lg);
  push('xl', xl);
  return parts.length ? parts.join(' ') : 'col';
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={cx(buildColCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, ...props }: any) => {
  const cls = cx('form-control', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Check = ({ type = 'checkbox', label, id, className, ...props }: any) => (
  <div className={cx('form-check', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
  </div>
);
Form.Text = ({ muted, className, children, ...props }: any) => (
  <small className={cx(muted && 'text-muted', className)} {...props}>
    {children}
  </small>
);
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Button as ShadPageButton } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import {
  API_CONTACT_GROUP_MEMBERS_CREATE,
  API_CONTACT_GROUP_MEMBERS_LIST,
} from '@/service/url';

const CreateAllContacts = () => {
  const noSpecialCharRegex = /^[a-zA-Z0-9 ]*$/;
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [contact, setContact] = useState({
    firstName: '',
    lastName: '',
    street: '',
    city: '',
    state: '',
    country: '',
    primaryNumber: '',
    isPrimaryNumber: false,
    alternateNumbers: [{ number: '', isPrimary: false }],
    company: '',
    isPrimary: true,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'primaryNumber') {
      const cleanedValue = value.replace(/\D/g, '').slice(0, 10);
      setContact({ ...contact, [name]: cleanedValue });
      return;
    }

    const fieldLabels = {
      firstName: 'First name',
      lastName: 'Last name',
      street: 'Street',
      city: 'City',
      state: 'State',
      country: 'Country',
      company: 'Company',
    };
    if (name in fieldLabels && !noSpecialCharRegex.test(value)) {
      toast.error(`${fieldLabels[name]} must not contain special characters`);
      return;
    }

    setContact({ ...contact, [name]: value });
  };

  const handlePrimaryNumberCheck = (e) => {
    const { checked } = e.target;
    setContact((prev) => ({
      ...prev,
      isPrimaryNumber: checked,
      alternateNumbers: prev.alternateNumbers.map((num) => ({ ...num, isPrimary: false })),
    }));
  };

  useEffect(() => {
    checkAuth(setToastProps, setShowToast);
  }, []);

  const handleAlternateNumberChange = (index, field, value) => {
    const newAlt = [...contact.alternateNumbers];

    if (field === 'isPrimary') {
      newAlt.forEach((num, i) => {
        num.isPrimary = i === index ? !newAlt[index].isPrimary : false;
      });
      setContact((prev) => ({
        ...prev,
        isPrimaryNumber: false,
        alternateNumbers: newAlt,
      }));
    } else {
      newAlt[index][field] = value;
      setContact((prev) => ({ ...prev, alternateNumbers: newAlt }));
    }
  };

  const addAlternateNumber = () => {
    setContact((prev) => ({
      ...prev,
      alternateNumbers: [...prev.alternateNumbers, { number: '', isPrimary: false }],
    }));
  };

  function onListSuccess(resp) {
    setContact(resp);
  }

  function onError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    const {
      firstName,
      lastName,
      primaryNumber,
      street,
      city,
      state,
      country,
      company,
      alternateNumbers = [],
    } = contact;

    const primaryAlternate = alternateNumbers.find((num) => num.isPrimary);
    const finalPrimaryNumber = primaryAlternate ? primaryAlternate.number : primaryNumber;

    const allNumbers = [
      ...alternateNumbers.filter((num) => !num.isPrimary).map((num) => num.number),
      ...(primaryAlternate ? [primaryNumber] : []),
    ].filter((num) => num && num.trim() !== '' && num !== finalPrimaryNumber);

    const [alt1_number, alt2_number] = allNumbers.slice(0, 2);

    if (!firstName.trim() || !finalPrimaryNumber.trim()) {
      toast.error('First name and primary number are required');
      return;
    }

    if (!noSpecialCharRegex.test(firstName.trim())) {
      toast.error('First name must not contain special characters');
      return;
    }
    if (city.trim() && !noSpecialCharRegex.test(city.trim())) {
      toast.error('City must not contain special characters');
      return;
    }

    const payload = {
      first_name: firstName,
      last_name: lastName,
      street,
      city,
      state,
      country,
      company,
      primary_number: finalPrimaryNumber,
      alt1_number: alt1_number || null,
      alt2_number: alt2_number || null,
    };

    const hasPrimaryAlternate = alternateNumbers.some((num) => num.isPrimary);
    if (!contact.isPrimaryNumber && !hasPrimaryAlternate) {
      toast.error('Please mark at least one phone number as primary');
      return;
    }

    postApiCall(API_CONTACT_GROUP_MEMBERS_CREATE, payload)
      .then((resp) => {
        onCreateSuccess(resp);
        setButtonDisable(false);
      })
      .catch((err) => {
        onCreateError(err);
        setButtonDisable(false);
      });
  };

  function onCreateSuccess() {
    toast.success('Contact Member created successfully');
    getApiCall(API_CONTACT_GROUP_MEMBERS_LIST, {}).then(onListSuccess).catch(onError);
    setTimeout(() => {
      navigate(`/all-contacts`);
    }, 1000);
  }

  function onCreateError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      setButtonDisable(false);
      toast.error(friendlyApiError(err) || 'Failed to create contact');
    }
  }

  const primaryLen = (contact.primaryNumber || '').length;

  return (
    <ScrollPage>
      <PageHeader
        title="Create Contact"
        description="Add a contact to any of your contact groups."
        actions={
          <ShadPageButton variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </ShadPageButton>
        }
      />
      <Card className="p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold text-foreground/80">
                First name <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                name="firstName"
                value={contact.firstName}
                onChange={handleInputChange}
                placeholder="Ex. John"
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-foreground/80">
                Last name
              </label>
              <input
                type="text"
                className="form-control"
                name="lastName"
                value={contact.lastName}
                onChange={handleInputChange}
                placeholder="Ex. Smith"
              />
            </div>
          </div>

          {/* Primary phone + Set-as-primary */}
          <div>
            <label className="mb-1 flex items-center justify-between text-xs font-semibold text-foreground/80">
              <span>
                Primary Phone Number <span className="text-destructive">*</span>
              </span>
              <label className="inline-flex cursor-pointer items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 accent-primary"
                  id="isPrimaryNumber"
                  name="isPrimaryNumber"
                  checked={contact.isPrimaryNumber}
                  onChange={handlePrimaryNumberCheck}
                />
                Use this as primary
              </label>
            </label>
            <input
              type="tel"
              className="form-control"
              name="primaryNumber"
              value={contact.primaryNumber}
              onChange={handleInputChange}
              placeholder="Ex. 0123456789"
              required
              maxLength={10}
              pattern="[0-9]{10}"
              onInput={(e: any) => {
                e.target.value = e.target.value.replace(/\D/g, '');
                if (e.target.value.length > 10) e.target.value = e.target.value.slice(0, 10);
              }}
            />
            <small
              className={
                'mt-1 block text-[11px] ' +
                (primaryLen === 10
                  ? 'text-emerald-700 dark:text-emerald-400'
                  : 'text-muted-foreground')
              }
            >
              {primaryLen === 10
                ? '✓ Valid 10-digit number'
                : 'Enter exactly 10 digits (numbers only)'}
            </small>
          </div>

          {/* Alternate phones */}
          {contact?.alternateNumbers?.map((alt, index) => (
            <div key={index}>
              <label className="mb-1 flex items-center justify-between text-xs font-semibold text-foreground/80">
                <span>
                  {index === 0
                    ? 'Alternate Phone Number'
                    : `Alternate Phone Number ${index + 1}`}
                </span>
                <label className="inline-flex cursor-pointer items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 accent-primary"
                    checked={alt.isPrimary}
                    onChange={() => handleAlternateNumberChange(index, 'isPrimary')}
                  />
                  Use this as primary
                </label>
              </label>
              <input
                type="tel"
                className="form-control"
                value={alt.number}
                onChange={(e) => {
                  const cleanedValue = e.target.value.replace(/\D/g, '').slice(0, 10);
                  handleAlternateNumberChange(index, 'number', cleanedValue);
                }}
                placeholder="Ex. 0123456789"
                maxLength={10}
                pattern="[0-9]{10}"
                onInput={(e: any) => {
                  e.target.value = e.target.value.replace(/\D/g, '');
                  if (e.target.value.length > 10) e.target.value = e.target.value.slice(0, 10);
                }}
              />
              <small
                className={
                  'mt-1 block text-[11px] ' +
                  (alt.number.length === 10
                    ? 'text-emerald-700 dark:text-emerald-400'
                    : 'text-muted-foreground')
                }
              >
                {alt.number.length === 10
                  ? '✓ Valid 10-digit number'
                  : 'Enter 10 digits (numbers only) — optional'}
              </small>
            </div>
          ))}

          {contact?.alternateNumbers?.length < 2 && (
            <div>
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={addAlternateNumber}
                className="gap-1"
              >
                <FiPlus className="h-4 w-4" />
                Add phone number
              </Button>
            </div>
          )}

          {/* Address */}
          <div>
            <label className="mb-1 block text-xs font-semibold text-foreground/80">
              Street
            </label>
            <input
              type="text"
              className="form-control"
              name="street"
              value={contact.street}
              onChange={handleInputChange}
              placeholder="Ex. Ninth Street"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-foreground/80">
                City
              </label>
              <input
                type="text"
                className="form-control"
                name="city"
                value={contact.city}
                onChange={handleInputChange}
                placeholder="Ex. New York"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-foreground/80">
                State / Province
              </label>
              <input
                type="text"
                className="form-control"
                name="state"
                value={contact.state}
                onChange={handleInputChange}
                placeholder="Ex. NY"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-foreground/80">
                Country
              </label>
              <input
                type="text"
                className="form-control"
                name="country"
                value={contact.country}
                onChange={handleInputChange}
                placeholder="Ex. USA"
              />
            </div>
          </div>

          {/* Company */}
          <div>
            <label className="mb-1 block text-xs font-semibold text-foreground/80">
              Company
            </label>
            <input
              type="text"
              className="form-control"
              name="company"
              value={contact.company}
              onChange={handleInputChange}
              placeholder="Ex. Voxtech Co"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="ghost" type="button" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button type="submit" disabled={buttonDisable}>
              {buttonDisable ? 'Creating…' : 'Create Contact'}
            </Button>
          </div>
        </form>
      </Card>
    </ScrollPage>
  );
};

export default CreateAllContacts;
